import React, { useEffect, useRef, useState, useContext } from "react"
import { useCurrentEditor, useCurrentNode } from "../../index"
import { getRootBlock } from "../../utils/helpers"
import { useActiveBlock } from "app/regions/hooks/use-active-block"
import { useBlocks } from "app/regions/hooks/use-blocks"
import { getSiblingsForBlock } from "app/regions/utils/block-traversal"
import { BlockContext } from "app/regions/context/block-context"
import { _editors } from "app/regions/blocks/text/text"

function getNextBlock(block_id, blocks) {
  const siblings = getSiblingsForBlock(block_id, blocks)
  const index = siblings.findIndex(s => s.block_id === block_id)
  return siblings[index + 1]
}

function focusEditorThroughTextBlock(block_id) {
  const block = document.querySelector(`[data-block-id="${block_id}"]`)
  const slate = block?.querySelector(`[contenteditable]`)
  slate?.focus()
}

/**
 * This method creates the list of items in the Block Menu.
 *
 * @param {function} options
 * @param {boolean} hasFocus
 * @param {function} onSelect
 */
function BlockMenuList({ options = () => [], onSelect = () => {}, onClick }) {
  const currentEditor = useCurrentEditor()
  const currentNode = useCurrentNode()
  const rootBlock = getRootBlock(currentEditor.value, currentNode)
  const ref = useRef(null)
  const [overlaps, setOverlaps] = useState(false)

  const { activeBlock } = useActiveBlock()
  const { add } = useBlocks(activeBlock.parent_id)
  const { blocks } = useContext(BlockContext)

  const handleSelect = async option => {
    const inTextBlock = activeBlock.type === "text"
    const addingBlock = option.type.endsWith("_block")
    const addingNode = !addingBlock

    if (inTextBlock || addingBlock) {
      onSelect(currentEditor, rootBlock, option)

      if (addingNode) {
        option.onSelect(currentEditor, rootBlock)
      }

      onClick && onClick()

      return
    }

    // If you're adding a node from outside of a text block...
    const nextBlock = getNextBlock(activeBlock.block_id, blocks)

    // If there's not already a text block, create one.
    let nextTextBlock = ""
    if (!nextBlock || nextBlock.type !== "text") {
      nextTextBlock = add({ type: "text" }, { position: activeBlock.order + 1 })
    } else {
      nextTextBlock = nextBlock.block_id
    }

    setTimeout(() => {
      onClick && onClick()
      focusEditorThroughTextBlock(nextTextBlock)
      const nextEditor = _editors[nextTextBlock]
      const nextRoot = getRootBlock(nextEditor.value)

      onSelect(nextEditor, nextRoot, option)
      option.onSelect(nextEditor, nextRoot, !inTextBlock)
    })
  }

  useEffect(() => {
    const rect = ref.current?.getBoundingClientRect()
    setOverlaps(rect?.bottom > window.innerHeight)
  }, [ref])

  const style = overlaps ? { bottom: 36 } : { top: 36 }

  return (
    <div
      ref={ref}
      id="block-menu-list"
      className="bg-white absolute left-0 rounded shadow p-2 overflow-y-scroll border border-gray-200 pointer-events-auto flex flex-col z-50"
      style={{ width: 210, maxHeight: 270, left: -24, ...style }}
    >
      {options(rootBlock).map((option, index) => (
        <button
          key={index}
          onClick={() => handleSelect(option)}
          className="my-1 p-1 rounded hover:bg-gray-100 duration-200 transition-colors focus:outline-blue"
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default BlockMenuList
